import {
  resolveBarbellStackView,
  isLikelyBarbellExerciseName,
  type PlateStrategy,
} from './barbell';

// One exercise reduced to what matters for an equipment checklist.
export type EquipmentExercise = {
  name: string;
  equipment: string[]; // catalog equipment tags (any case)
  weights: number[]; // planned weights (kg) for this exercise
};

export type BarbellLoad = {
  totalKg: number;
  perSide: number[] | null; // plates per side (kg); null = not cleanly loadable, [] = empty bar
  barWeight: number;
};

export type EquipmentPlan = {
  barbell: { barWeight: number; loads: BarbellLoad[]; owned: boolean } | null;
  dumbbells: { used: boolean; weights: number[]; owned: boolean };
  kettlebells: { used: boolean; weights: number[]; owned: boolean };
  plates: number[]; // distinct plate denominations needed (kg), descending
  // prettified labels for everything else + whether the user owns it (green check)
  other: Array<{ label: string; owned: boolean }>;
  isEmpty: boolean;
};

const PRETTY: Record<string, string> = {
  band: 'Resistance band',
  bands: 'Resistance band',
  'resistance band': 'Resistance band',
  'mini band': 'Mini band',
  'pull-up bar': 'Pull-up bar',
  'pull up bar': 'Pull-up bar',
  'pullup bar': 'Pull-up bar',
  box: 'Plyo box',
  'plyo box': 'Plyo box',
  'jump box': 'Plyo box',
  bench: 'Bench',
  'flat bench': 'Bench',
  'incline bench': 'Incline bench',
  'wall ball': 'Wall ball',
  'medicine ball': 'Medicine ball',
  'med ball': 'Medicine ball',
  'slam ball': 'Slam ball',
  'jump rope': 'Jump rope',
  'jump-rope': 'Jump rope',
  rope: 'Climbing rope',
  'climbing rope': 'Climbing rope',
  rower: 'Rower',
  'row erg': 'Rower',
  ski: 'Ski erg',
  'ski erg': 'Ski erg',
  bike: 'Bike',
  'assault bike': 'Assault bike',
  'echo bike': 'Echo bike',
  cable: 'Cable machine',
  'cable machine': 'Cable machine',
  'foam roller': 'Foam roller',
  'ab wheel': 'Ab wheel',
  rings: 'Gymnastic rings',
  'gymnastic rings': 'Gymnastic rings',
  sled: 'Sled',
  'trap bar': 'Trap bar',
  'hex bar': 'Trap bar',
  landmine: 'Landmine',
  // The title-case fallback below would already produce these two, but the
  // PreparePanel builds its German lookup key from the label (`equipment:${label}`),
  // so an explicit entry is what pins the key the translation is filed under.
  tire: 'Tire',
  yoke: 'Yoke',
  ghd: 'GHD machine',
  'back extension bench': 'Back-extension bench',
  parallettes: 'Parallettes',
};

export function prettyEquipment(tag: string): string {
  const t = tag.trim().toLowerCase();
  if (PRETTY[t]) return PRETTY[t];
  return t.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Can someone owning `owned` perform an exercise tagged `required`?
 *
 * Matching is subset, not overlap: every tag is something the movement *needs*.
 * The one exception is dumbbell + kettlebell together, which computeEquipment
 * emits to mean "either will do" (Farmer Carry, Overhead Carry, and 95 rows
 * besides) — so that pair alone is an OR. Barbell is NOT part of it: a bar is not
 * a substitute for a dumbbell, and treating it as one showed barbell owners the
 * dumbbell and kettlebell Overhead Squat.
 *
 * Mirrors `canPerformWith` in apps/api/src/routes/aiPlan.ts, with one deliberate
 * difference: the planner treats 'bodyweight' as free, while here it must be
 * ticked. The Equipment picker lists it, so leaving it unticked is an answer.
 */
export function canPerformWith(required: string[], owned: ReadonlySet<string>): boolean {
  if (!required.length) return false; // untagged: unknowable, so not offered
  const eitherLoad = required.includes('dumbbell') && required.includes('kettlebell');
  if (eitherLoad && !owned.has('dumbbell') && !owned.has('kettlebell')) return false;
  return required.every(
    (eq) => owned.has(eq) || (eitherLoad && (eq === 'dumbbell' || eq === 'kettlebell')),
  );
}

type Kind = 'barbell' | 'dumbbell' | 'kettlebell' | null;

function classify(name: string, equipment: string[]): Kind {
  const n = String(name).toLowerCase();
  const eq = equipment.map((s) => String(s).toLowerCase());
  const has = (k: string) => eq.some((e) => e.includes(k));

  // 1) The exercise NAME is the strongest signal for the *specific* implement.
  //    Most kettlebell exercises are ALSO tagged 'dumbbell' (a valid alternative),
  //    so we must read the name before the tags — otherwise "Kettlebell Swing"
  //    shows a Dumbbell. Likewise "Dumbbell Snatch" must win over any kb tag.
  if (/\bkettlebells?\b|\bkb\b/.test(n)) return 'kettlebell';
  if (/\bdumbbells?\b|\bdb\b/.test(n)) return 'dumbbell';
  if (/turkish get-?up/.test(n)) return 'kettlebell'; // iconic KB move, often tagged both
  if (isLikelyBarbellExerciseName(name)) return 'barbell';

  // 2) Fall back to catalog tags for exercises whose name doesn't name an implement.
  if (has('barbell')) return 'barbell';
  if (has('dumbbell')) return 'dumbbell';
  if (has('kettlebell')) return 'kettlebell';
  return null;
}

// Tags that are never "other equipment" the user has to fetch.
const SKIP_OTHER = new Set(['barbell', 'dumbbell', 'kettlebell', 'bodyweight', 'machine', 'none', 'other', 'gym']);

const round1 = (n: number) => Math.round(n * 10) / 10;

export function analyzeEquipment(
  exercises: EquipmentExercise[],
  strategy: PlateStrategy = 'paired',
  owned: string[] = []
): EquipmentPlan {
  const ownedSet = new Set(owned.map((s) => String(s).trim().toLowerCase()));
  const isOwned = (tag: string) => ownedSet.has(tag);
  let barbellUsed = false;
  let dumbbellUsed = false;
  let kettlebellUsed = false;
  const barbellLoads = new Set<number>();
  const dumbbellW = new Set<number>();
  const kettlebellW = new Set<number>();
  const other = new Map<string, boolean>(); // prettified label -> owned

  for (const ex of exercises) {
    const kind = classify(ex.name, ex.equipment);
    const weights = (ex.weights || []).filter(
      (w) => typeof w === 'number' && Number.isFinite(w) && w > 0
    );
    if (kind === 'barbell') {
      barbellUsed = true;
      weights.forEach((w) => barbellLoads.add(round1(w)));
    } else if (kind === 'dumbbell') {
      dumbbellUsed = true;
      weights.forEach((w) => dumbbellW.add(round1(w)));
    } else if (kind === 'kettlebell') {
      kettlebellUsed = true;
      weights.forEach((w) => kettlebellW.add(round1(w)));
    }

    for (const raw of ex.equipment || []) {
      const tag = String(raw).trim().toLowerCase();
      if (!tag || SKIP_OTHER.has(tag)) continue;
      if (tag.includes('barbell') || tag.includes('dumbbell') || tag.includes('kettlebell')) continue;
      const label = prettyEquipment(tag);
      other.set(label, (other.get(label) ?? false) || isOwned(tag));
    }
  }

  const plateSet = new Set<number>();
  let barbell: EquipmentPlan['barbell'] = null;
  if (barbellUsed) {
    const loads: BarbellLoad[] = [...barbellLoads]
      .sort((a, b) => a - b)
      .map((totalKg) => {
        const view = resolveBarbellStackView({ totalKg, strategy });
        const perSide = view ? view.plates : null;
        if (perSide) perSide.forEach((p) => plateSet.add(p));
        return { totalKg, perSide, barWeight: view?.barWeight ?? 20 };
      });
    barbell = { barWeight: 20, loads, owned: isOwned('barbell') };
  }

  const otherList = [...other.entries()]
    .map(([label, owned]) => ({ label, owned }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return {
    barbell,
    dumbbells: { used: dumbbellUsed, weights: [...dumbbellW].sort((a, b) => a - b), owned: isOwned('dumbbell') },
    kettlebells: { used: kettlebellUsed, weights: [...kettlebellW].sort((a, b) => a - b), owned: isOwned('kettlebell') },
    plates: [...plateSet].sort((a, b) => b - a),
    other: otherList,
    isEmpty: !barbellUsed && !dumbbellUsed && !kettlebellUsed && otherList.length === 0,
  };
}
